import { useGetList, useTranslate, type Identifier } from "ra-core";
import { Badge } from "@/components/ui/badge";
import type { CampaignTarget } from "../types";

export const CampaignTargetBadges = ({
  campaignId,
}: {
  campaignId: Identifier;
}) => {
  const translate = useTranslate();
  const { data: targets } = useGetList<CampaignTarget>("campaign_targets", {
    filter: { campaign_id: campaignId },
    pagination: { page: 1, perPage: 500 },
  });
  const { data: segments } = useGetList("bc_segments", {
    pagination: { page: 1, perPage: 200 },
  });
  const { data: companies } = useGetList("companies", {
    pagination: { page: 1, perPage: 500 },
  });
  const { data: contacts } = useGetList("contacts", {
    pagination: { page: 1, perPage: 500 },
  });
  const { data: tags } = useGetList("tags", {
    pagination: { page: 1, perPage: 200 },
  });

  if (!targets?.length) {
    return (
      <p className="text-sm text-muted-foreground">
        {translate("resources.campaigns.targets.empty", {
          _: "No campaign targets configured yet.",
        })}
      </p>
    );
  }

  const labelFor = (target: CampaignTarget) => {
    if (target.target_type === "segment") {
      const seg = segments?.find((s) => s.id === target.bc_segment_id);
      return `Segment: ${seg?.description ?? seg?.number ?? target.bc_segment_id}`;
    }
    if (target.target_type === "company") {
      const company = companies?.find((c) => c.id === target.company_id);
      return `Company: ${company?.name ?? target.company_id}`;
    }
    if (target.target_type === "tag") {
      const tag = tags?.find((t) => t.id === target.tag_id);
      return `Tag: ${tag?.name ?? target.tag_id}`;
    }
    const contact = contacts?.find((c) => c.id === target.contact_id);
    const name = contact
      ? `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim()
      : `#${target.contact_id}`;
    return `Contact: ${name}`;
  };

  return (
    <div className="flex flex-wrap gap-2">
      {targets.map((target) => (
        <Badge key={target.id} variant="secondary">
          {labelFor(target)}
        </Badge>
      ))}
    </div>
  );
};
